import moment from 'moment';

import {assert} from 'Asserts';
import {prependAOrAn} from 'BenchlingUtil';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys} from 'procedures/ProceduresStringKeys';
import {getProcedureTypeForStudyType, getStudyTypeText} from 'studies/TypedStudyUtils';
import * as DateUtil from 'util/DateUtil';

import {StudyListingGraphQL} from '../../../../../graphql-fragments/studies/__generated/StudyListingGraphQL';

export const CREATE_ANALYSIS_NO_PERMISSION_MESSAGE =
  'You do not have sufficient permissions to create an analysis. Please contact your administrator.';

export function getCannotCreateAnalysisMessageForStudyType(studyType: StudyType): string {
  const studyTypeText = getStudyTypeText(studyType);
  const applicationName = getProcedureUIString(
    ProceduresStringKeys.PROCEDURE,
    getProcedureTypeForStudyType(studyType),
    {plural: true}
  );
  return `Analyzing ${prependAOrAn(studyTypeText)} study requires access to the ${applicationName} application. Contact your administrator for access.`;
}
export const NO_PROCEDURE_RUNS_TO_ANALYZE_MESSAGE = 'No runs available to analyze yet.';

export function getCommonStudyType(studyTypes: Array<StudyType>): StudyType | null {
  if (studyTypes.length === 0) {
    return null;
  }
  const uniqueTypes = new Set(studyTypes);
  if (uniqueTypes.size !== 1) {
    return null;
  }
  return studyTypes[0];
}

export function getTooManyStudiesMessage(limit: number, count: number): string {
  const studiesToRemove = count - limit;
  return `You can analyze up to ${limit} studies at a time. Remove ${studiesToRemove} to continue.`;
}

/**
 * Returns the initial analysis name to use in the create analysis modal for analysis of a study.
 *
 * @param studyName The name of the study.
 */
export function getInitialAnalysisNameForStudy(studyName: string): string {
  return `${studyName}_${DateUtil.readableTime(moment(), {
    showTime: true,
  })}`;
}

/**
 * Formats a name with a timestamp, using an "_and_N_others" suffix when there are multiple items.
 */
function formatNameWithTimestamp(firstName: string, totalCount: number, timestamp: string): string {
  if (totalCount === 1) {
    return `${firstName}_${timestamp}`;
  }
  return `${firstName}_and_${totalCount - 1}_others_${timestamp}`;
}

/**
 * Returns the initial analysis name to use in the create analysis modal for cross-study analysis.
 *
 * @param selectedStudies The studies to be analyzed.
 */
export function getInitialAnalysisNameForCrossStudyAnalysis(
  selectedStudies: Array<StudyListingGraphQL>
): string {
  assert(selectedStudies.length > 0, 'Expected multiple studies to be selected.');
  const timestamp = DateUtil.readableTime(moment(), {showTime: true});
  return formatNameWithTimestamp(selectedStudies[0].name, selectedStudies.length, timestamp);
}

export function getInitialAnalysisNameForProcedureRun(studyName: string, procedureRunName: string): string {
  return `${studyName}_${procedureRunName}_${DateUtil.readableTime(moment(), {
    showTime: true,
  })}`;
}

export function getInitialAnalysisNameForSelectedProcedureRuns(
  studyName: string,
  procedureRunNames: Array<string>
): string {
  assert(procedureRunNames.length > 0, 'Expected at least one procedure run to be selected.');
  const timestamp = DateUtil.readableTime(moment(), {showTime: true});
  return `${studyName}_${formatNameWithTimestamp(procedureRunNames[0], procedureRunNames.length, timestamp)}`;
}
